import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { BRAND, PROJECTS } from "@/data/content";
import { Reveal, SectionLabel } from "@/components/site/Reveal";

const PortfolioPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1428] text-white">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A1428]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="portfolio-logo">
            <img src={BRAND.logo} alt={BRAND.name} className="h-10 w-10 rounded-lg object-contain" />
            <span className="font-display font-bold text-lg">TITAN<span className="text-[#1E6BFF]">.</span></span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors" data-testid="portfolio-back-home">
              <span className="inline-flex items-center gap-2"><ArrowLeft size={16} /> Home</span>
            </Link>
            <Link
              to="/#contact"
              data-testid="portfolio-plan-cta"
              className="hidden sm:inline-flex items-center rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white text-sm font-semibold px-6 py-2.5 transition-colors duration-200 glow-blue"
            >
              Get a Free Plan
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-24 md:pb-36">
        {/* Header */}
        <div className="mb-16 md:mb-20">
          <SectionLabel testid="portfolio-page-label">Portfolio</SectionLabel>
          <Reveal>
            <h1 className="font-display font-black uppercase tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.92]">
              Selected work.
              <br />
              <span className="text-[#1E6BFF]">Real growth.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-gray-300 leading-relaxed">
              Every project below is engineered around a single number that matters to the owner —
              bookings, revenue, or qualified leads. Tap in to see the challenge, the build, and the
              results.
            </p>
          </Reveal>
        </div>

        {/* Grid — larger, editorial */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.05}>
              <Link to={`/work/${p.slug}`} data-testid={`portfolio-project-${p.slug}`} className="group block">
                <motion.div
                  whileHover="hover"
                  className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#111827]"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <motion.img
                      variants={{ hover: { scale: 1.06 } }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1428] via-[#0A1428]/25 to-transparent" />
                    <div className="absolute top-6 right-6 w-12 h-12 rounded-full bg-[#1E6BFF] flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                      <ArrowUpRight size={22} className="text-white" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex items-end justify-between gap-4">
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#1E6BFF] font-semibold mb-2">
                          {p.category}
                        </div>
                        <h3 className="font-display font-semibold text-2xl md:text-3xl text-white">
                          {p.title}
                        </h3>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-bold text-lg md:text-2xl text-white">
                          {p.result}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Bottom CTA */}
        <Reveal delay={0.15}>
          <div className="mt-20 md:mt-28 rounded-3xl border border-[#1E6BFF]/30 bg-gradient-to-r from-[#1E6BFF]/10 to-transparent p-10 md:p-16 text-center">
            <h2 className="font-display font-bold tracking-tight text-white text-3xl md:text-5xl leading-[1.05]">
              Ready to be our next case study?
            </h2>
            <p className="mt-4 text-gray-300 max-w-xl mx-auto">
              Free 30-minute strategy call. Bring your goals — leave with a plan.
            </p>
            <Link
              to="/#contact"
              data-testid="portfolio-final-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white font-semibold px-8 py-4 transition-colors duration-200 glow-blue"
            >
              Get a Free Website Plan <ArrowUpRight size={18} />
            </Link>
          </div>
        </Reveal>
      </main>
    </div>
  );
};

export default PortfolioPage;
